<script setup lang="ts">
const { modelValue, maxWidth, maxHeight, zIndex, scrollable = true } = defineProps<{
  modelValue?: boolean
  maxWidth?: string
  maxHeight?: string
  title?: string
  offsetTop?: string
  offsetBottom?: string
  disableCloseButton?: boolean
  zIndex?: string | number
  scrollable?: boolean
  preload?: boolean
}>()

const emit = defineEmits(['update:modelValue'])

function hideScroll() {
  document.documentElement.style.setProperty('overflow', 'hidden')
  document.body.style.setProperty('overflow-y', 'scroll')
}
function showScroll() {
  document.documentElement.style.removeProperty('overflow')
  document.body.style.removeProperty('overflow')
}

watch(() => modelValue, visible => visible ? hideScroll() : showScroll())
onMounted(() => modelValue && hideScroll())
onUnmounted(() => showScroll())

defineComponent({
  inheritAttrs: false,
})
</script>

<template>
  <teleport v-if="modelValue || preload" to="body">
    <div v-show="modelValue">
      <div class="fixed overflow-auto inset-0 bg-black bg-opacity-25 grid place-items-center p-5" :style="{ paddingTop: offsetTop, paddingBottom: offsetBottom, zIndex: Number(zIndex) || 14 }">
        <div v-bind="$attrs" class="bg-white rounded-xl py-5 flex flex-col gap-5 min-w-280px xs:min-w-335px " :style="{ maxWidth: maxWidth || '600px', maxHeight: maxHeight || 'calc(100vh - 40px)' }">
          <!-- HEADER -->
          <div class="flex headline-3 items-center justify-between px-5">
            <slot name="title">
              {{ title }}
            </slot>
            <AppIcon v-if="!disableCloseButton" size="23" icon="far-times" class="cursor-pointer self-start" @click="emit('update:modelValue', false)" />
          </div>
          <div v-if="$slots.header" class="px-5">
            <slot name="header" />
          </div>
          <!-- BODY -->
          <div class="scrollbar-hide scroll-shadows px-5" :class="{ 'overflow-scroll': scrollable }">
            <slot />
          </div>
          <!-- FOOTER -->
          <div v-if="$slots.footer" class="px-5">
            <slot name="footer" />
          </div>
        </div>
      </div>
    </div>
  </teleport>
</template>

<style scoped>
.scroll-shadows {
  background:
    -webkit-gradient(linear,left top, left bottom,color-stop(30%, white),to(white)) center top,
    -webkit-gradient(linear,left top, left bottom,from(white),color-stop(70%, white)) center bottom,
    -webkit-gradient(linear, left top, left bottom, from(theme('colors.secondary')26), to(theme('colors.secondary')00)) center top,
    -webkit-gradient(linear, left top, left bottom, from(theme('colors.secondary')00), to(theme('colors.secondary')26)) center bottom;
  background:
    -o-linear-gradient(white 30%,white) center top,
    -o-linear-gradient(white,white 70%) center bottom,
    -o-linear-gradient(theme('colors.secondary'26), theme('colors.secondary')00) center top,
    -o-linear-gradient(theme('colors.secondary'00), theme('colors.secondary')26) center bottom;
  background:
    linear-gradient(white 30%,white) center top,
    linear-gradient(white,white 70%) center bottom,
    linear-gradient(theme('colors.secondary')26, theme('colors.secondary')00) center top,
    linear-gradient(theme('colors.secondary')00, theme('colors.secondary')26) center bottom;
  background-repeat: no-repeat;
  background-color: white;
  background-size: 100% 20px, 100% 20px, 100% 5px, 100% 5px;
  background-attachment: local, local, scroll, scroll;
}
</style>
